import React, { createContext, useState, useEffect, useCallback } from "react";
import {
  PRODUCTION_URL,
  SESSION_KEY,
  AUTH_TIMESTAMP_KEY,
  PASSWORD,
  SESSION_TIMEOUT,
} from "./model";

export const DevAuthContext = createContext<{
  isAuthenticated: boolean;
  logout: () => void;
}>({
  isAuthenticated: false,
  logout: () => {},
});

const DevAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(AUTH_TIMESTAMP_KEY);
    setIsAuthenticated(false);
  }, []);

  const checkSessionValidity = useCallback(() => {
    const authTimestamp = localStorage.getItem(AUTH_TIMESTAMP_KEY);
    if (!authTimestamp) return false;

    const elapsed = Date.now() - parseInt(authTimestamp, 10);
    return elapsed < SESSION_TIMEOUT;
  }, []);

  useEffect(() => {
    // Check if user is authenticated and session is valid
    const checkAuth = () => {
      const hasAuth = localStorage.getItem(SESSION_KEY) === "true";

      if (hasAuth && !checkSessionValidity()) {
        // Session expired
        logout();
        return;
      }

      setIsAuthenticated(hasAuth);
    };

    checkAuth();

    // Set up interval to check session validity
    const interval = setInterval(checkAuth, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [logout, checkSessionValidity]);

  return (
    <DevAuthContext.Provider value={{ isAuthenticated, logout }}>
      {isAuthenticated ? children : <DevAuthGate />}
    </DevAuthContext.Provider>
  );
};

// Simple component to show when not authenticated in stage
const DevAuthGate = () => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === PASSWORD) {
      localStorage.setItem(SESSION_KEY, "true");
      localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
      window.location.reload();
    } else {
      setError("Contraseña incorrecta. Inténtalo de nuevo.");
    }
  };

  return (
    <div
      className="dev-auth-gate"
      style={{
        padding: "0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: "#f9fafb", // Tailwind gray-50
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "0.5rem",
          padding: "1.5rem",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          width: "90%",
          maxWidth: "400px",
        }}
      >
        <div>
          <h2
            style={{
              color: "#111827", // Tailwind gray-900
              fontSize: "1.25rem",
              fontWeight: "600",
              textAlign: "center",
              marginBottom: "1rem",
            }}
          >
            Entorno de Pruebas
          </h2>
        </div>

        <div
          style={{
            marginTop: "0.5rem",
            marginBottom: "1rem",
            padding: "1rem",
            backgroundColor: "#fffbeb", // Tailwind amber-50
            borderRadius: "0.375rem",
            fontSize: "0.75rem",
            lineHeight: "1.25rem",
            color: "#713f12", // Tailwind amber-900
          }}
        >
          <p style={{ margin: "0 0 0.5rem 0" }}>
            <strong>Estás ingresando a un entorno de pruebas (sandbox).</strong>
          </p>
          <p style={{ margin: "0 0 0.5rem 0" }}>
            La información que verás aquí es ficticia y se utiliza únicamente
            con fines de prueba y demostración. Ningún dato es real y ninguna
            acción tiene efectos en sistemas en producción. Por favor, no
            ingreses información personal o confidencial.
          </p>
          <p style={{ margin: "0 0 0.5rem 0" }}>
            En caso de no recordar la contraseña, debe ponerse en contacto con
            el equipo de IT de RE/MAX RD.
          </p>
          <p style={{ margin: "0" }}>
            Si estás buscando la página real, haz clic en el siguiente enlace:{" "}
            <a
              href={PRODUCTION_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#b91c1c", // Tailwind red-700
                textDecoration: "underline",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              {PRODUCTION_URL}
            </a>
          </p>
        </div>

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: "1rem" }}>
            <label
              htmlFor="password"
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
                fontSize: "0.875rem",
                color: "#374151", // Tailwind gray-700
              }}
            >
              Contraseña
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem 0.75rem",
                  paddingRight: "2.5rem", // Space for the eye icon
                  border: "1px solid #d1d5db", // Tailwind gray-300
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
                placeholder="Ingresa la contraseña"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  padding: "0",
                  cursor: "pointer",
                  color: "#6b7280", // Tailwind gray-500
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label={
                  showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                }
              >
                {showPassword ? (
                  // Eye-off icon (when password is visible)
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  // Eye icon (when password is hidden)
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div
              style={{
                color: "#ef4444", // Tailwind red-500
                fontSize: "0.875rem",
                marginBottom: "1rem",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            style={{
              width: "100%",
              padding: "0.625rem 1rem",
              backgroundColor: "#ef4444", // Tailwind red-500
              color: "white",
              border: "none",
              borderRadius: "0.375rem",
              fontSize: "0.875rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "background-color 0.2s",
              marginTop: "1rem",
            }}
          >
            Acceder
          </button>
        </form>
      </div>
    </div>
  );
};

export { DevAuthProvider };
